import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen } from "lucide-react";

// Weather and outlook themed Bible verses
const WEATHER_VERSES = [
  {
    reference: "Matthew 5:45",
    short: "He causes His sun to rise on the evil and the good...",
    full: "He causes His sun to rise on the evil and the good, and sends rain on the righteous and the unrighteous.",
  },
  {
    reference: "Psalm 147:8",
    short: "He covers the sky with clouds; He supplies the earth with rain...",
    full: "He covers the sky with clouds; He supplies the earth with rain and makes grass grow on the hills.",
  },
  {
    reference: "Job 37:5-6",
    short: "God's voice thunders in marvelous ways...",
    full: "God's voice thunders in marvelous ways; He does great things beyond our understanding. He says to the snow, 'Fall on the earth,' and to the rain shower, 'Be a mighty downpour.'",
  },
  {
    reference: "Jeremiah 10:13",
    short: "When He thunders, the waters in the heavens roar...",
    full: "When He thunders, the waters in the heavens roar; He makes clouds rise from the ends of the earth. He sends lightning with the rain and brings out the wind from His storehouses.",
  },
  {
    reference: "Psalm 19:1",
    short: "The heavens declare the glory of God...",
    full: "The heavens declare the glory of God; the skies proclaim the work of His hands.",
  },
  {
    reference: "Matthew 16:2-3",
    short: "When evening comes, you say, 'It will be fair weather...'",
    full: "When evening comes, you say, 'It will be fair weather, for the sky is red,' and in the morning, 'Today it will be stormy, for the sky is red and overcast.' You know how to interpret the appearance of the sky.",
  },
  {
    reference: "Nahum 1:3",
    short: "His way is in the whirlwind and the storm...",
    full: "The LORD is slow to anger but great in power; the LORD will not leave the guilty unpunished. His way is in the whirlwind and the storm, and clouds are the dust of His feet.",
  },
  {
    reference: "Job 38:22-23",
    short: "Have you entered the storehouses of the snow...",
    full: "Have you entered the storehouses of the snow or seen the storehouses of the hail, which I reserve for times of trouble, for days of war and battle?",
  },
  {
    reference: "Psalm 135:7",
    short: "He makes clouds rise from the ends of the earth...",
    full: "He makes clouds rise from the ends of the earth; He sends lightning with the rain and brings out the wind from His storehouses.",
  },
  {
    reference: "Isaiah 55:10",
    short: "As the rain and the snow come down from heaven...",
    full: "As the rain and the snow come down from heaven, and do not return to it without watering the earth and making it bud and flourish, so that it yields seed for the sower and bread for the eater.",
  },
  {
    reference: "Ecclesiastes 11:4",
    short: "Whoever watches the wind will not plant...",
    full: "Whoever watches the wind will not plant; whoever looks at the clouds will not reap.",
  },
  {
    reference: "James 5:17-18",
    short: "Elijah prayed earnestly that it would not rain...",
    full: "Elijah was a human being, even as we are. He prayed earnestly that it would not rain, and it did not rain on the land for three and a half years. Again he prayed, and the heavens gave rain, and the earth produced its crops.",
  },
  {
    reference: "Psalm 104:3",
    short: "He makes the clouds His chariot...",
    full: "He lays the beams of His upper chambers on their waters. He makes the clouds His chariot and rides on the wings of the wind.",
  },
  {
    reference: "Genesis 9:13-14",
    short: "I have set My rainbow in the clouds...",
    full: "I have set My rainbow in the clouds, and it will be the sign of the covenant between Me and the earth. Whenever I bring clouds over the earth and the rainbow appears in the clouds.",
  },
  {
    reference: "Proverbs 25:14",
    short: "Like clouds and wind without rain...",
    full: "Like clouds and wind without rain is one who boasts of gifts never given.",
  },
  {
    reference: "Luke 12:54-55",
    short: "When you see a cloud rising in the west...",
    full: "When you see a cloud rising in the west, immediately you say, 'It's going to rain,' and it does. And when the south wind blows, you say, 'It's going to be hot,' and it is.",
  },
  {
    reference: "Amos 4:7",
    short: "I withheld rain from you when the harvest was still three months away...",
    full: "I also withheld rain from you when the harvest was still three months away. I sent rain on one town, but withheld it from another. One field had rain; another had none and dried up.",
  },
  {
    reference: "Job 36:27-28",
    short: "He draws up the drops of water...",
    full: "He draws up the drops of water, which distill as rain to the streams; the clouds pour down their moisture and abundant showers fall on mankind.",
  },
  {
    reference: "Psalm 148:8",
    short: "Lightning and hail, snow and clouds...",
    full: "Lightning and hail, snow and clouds, stormy winds that do His bidding.",
  },
  {
    reference: "Zechariah 10:1",
    short: "Ask the LORD for rain in the springtime...",
    full: "Ask the LORD for rain in the springtime; it is the LORD who sends the thunderstorms. He gives showers of rain to all people, and plants of the field to everyone.",
  }
];

export default function VerseOfTheDay() {
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Get a consistent verse for the day based on the date
  const today = new Date();
  const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
  const verse = WEATHER_VERSES[dayOfYear % WEATHER_VERSES.length];
  
  return (
    <div className="flex justify-center items-center mb-4">
      <AnimatePresence mode="wait">
        {!isHovered && !isExpanded ? (
          // Icon Only State
          <motion.button
            key="icon"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onMouseEnter={() => setIsHovered(true)}
            onClick={() => setIsExpanded(true)}
            className="p-2.5 rounded-full glass bg-white/5 hover:bg-white/10 transition-all cursor-pointer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <BookOpen className="w-5 h-5 text-white/50 hover:text-[#ccff00] transition-colors" />
          </motion.button>
        ) : isHovered && !isExpanded ? (
          // Hover State - Short verse
          <motion.div
            key="hover"
            initial={{ opacity: 0, width: 40 }}
            animate={{ opacity: 1, width: "auto" }}
            exit={{ opacity: 0, width: 40 }}
            transition={{ duration: 0.2 }}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => setIsExpanded(true)}
            className="glass rounded-full px-4 py-2.5 flex items-center gap-3 cursor-pointer hover:bg-white/5 transition-all"
          >
            <BookOpen className="w-5 h-5 text-[#ccff00] flex-shrink-0" />
            <p className="text-sm text-white/70 italic whitespace-nowrap">"{verse.short}"</p>
            <span className="text-xs text-[#ccff00]/70 whitespace-nowrap">— {verse.reference}</span>
          </motion.div>
        ) : (
          // Expanded State - Full verse
          <motion.div
            key="expanded"
            initial={{ opacity: 0, width: 200 }}
            animate={{ opacity: 1, width: "auto" }}
            exit={{ opacity: 0, width: 200 }}
            transition={{ duration: 0.25 }}
            onClick={() => {
              setIsExpanded(false);
              setIsHovered(false);
            }}
            className="glass rounded-2xl px-6 py-4 flex items-center gap-4 cursor-pointer hover:bg-white/5 transition-all max-w-4xl"
          >
            <BookOpen className="w-6 h-6 text-[#ccff00] flex-shrink-0" />
            <div className="flex items-center gap-4">
              <p className="text-sm text-white/90 italic leading-relaxed">"{verse.full}"</p>
              <div className="flex-shrink-0 pl-4 border-l border-white/10">
                <p className="text-xs text-[#ccff00] font-medium whitespace-nowrap">{verse.reference}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
